import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Receipt, Search } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Invoice,
  InvoiceCurrency,
  InvoiceStatus,
  useCreateInvoice,
  useInvoices,
} from "@/hooks/useInvoices";
import { useProjects } from "@/hooks/useProjects";
import { InvoiceDetailView } from "@/components/project-detail/finance/InvoiceDetailView";
import { formatCurrency } from "@/lib/utils";
import { formatProjectName } from "@/lib/format-project-name";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "@/lib/languages";

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: "Draft",
  sent: "Unpaid",
  paid: "Paid",
  cancelled: "Cancelled",
};

const STATUS_CLASSES: Record<InvoiceStatus, string> = {
  draft: "",
  sent: "bg-blue-600",
  paid: "bg-green-600",
  cancelled: "",
};

function statusBadge(invoice: Invoice) {
  return (
    <Badge
      variant={invoice.status === "cancelled" ? "destructive" : "secondary"}
      className={STATUS_CLASSES[invoice.status]}
    >
      {STATUS_LABELS[invoice.status]}
    </Badge>
  );
}

function formatDate(date: string | null) {
  if (!date) return "-";
  return new Date(`${date}T00:00:00`).toLocaleDateString("de-CH");
}

function invoiceProjectLabel(invoice: Invoice) {
  if (!invoice.project) return "Standalone";
  return [invoice.project.number, invoice.project.name]
    .filter(Boolean)
    .join(" ");
}

export default function FinancePage() {
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const { data: invoices = [], isLoading } = useInvoices();
  const { data: projects = [] } = useProjects();
  const createInvoice = useCreateInvoice();

  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [projectId, setProjectId] = useState("none");
  const [language, setLanguage] = useState<SupportedLanguage>("en");
  const [currency, setCurrency] = useState<InvoiceCurrency>("CHF");
  const [query, setQuery] = useState("");
  const [statuses, setStatuses] = useState<Record<InvoiceStatus, boolean>>({
    draft: true,
    sent: true,
    paid: false,
    cancelled: false,
  });

  const selectedProject = projects.find((project) => project.id === projectId);

  const filteredInvoices = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return invoices.filter((invoice) => {
      if (!statuses[invoice.status]) return false;
      if (!needle) return true;
      return [
        invoice.title,
        invoice.invoice_number,
        invoiceProjectLabel(invoice),
        invoice.customer?.name,
        invoice.contact
          ? `${invoice.contact.first_name} ${invoice.contact.last_name}`
          : "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [invoices, query, statuses]);

  const handleCreate = () => {
    const project = selectedProject;
    const invoiceTitle =
      title.trim() ||
      (project
        ? formatProjectName(project.name, project.event_start_date)
        : "Standalone invoice");

    createInvoice.mutate(
      {
        projectId: project?.id ?? null,
        title: invoiceTitle,
        language: project ? null : language,
        currency,
        projectDiscountPercent: project?.default_discount_percent ?? 0,
        vatRate: (project?.default_vat_rate as "CH_8_1" | "NONE") ?? "CH_8_1",
      },
      {
        onSuccess: (invoice) => {
          setCreateOpen(false);
          setTitle("");
          setProjectId("none");
          setLanguage("en");
          setCurrency("CHF");
          navigate(`/finance/invoices/${invoice.id}`);
        },
      },
    );
  };

  if (invoiceId) {
    return (
      <div className="p-6">
        <InvoiceDetailView
          invoiceId={invoiceId}
          onBack={() => navigate("/finance")}
        />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <PageHeader title="Finance" />
        <Button offlineEdit onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Invoice
        </Button>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="relative md:w-80">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search invoices"
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-4">
          {(Object.keys(STATUS_LABELS) as InvoiceStatus[]).map((status) => (
            <label
              key={status}
              className="flex items-center gap-2 text-sm text-muted-foreground"
            >
              <Checkbox
                checked={statuses[status]}
                onCheckedChange={(checked) =>
                  setStatuses((current) => ({
                    ...current,
                    [status]: checked === true,
                  }))
                }
              />
              {STATUS_LABELS[status]}
            </label>
          ))}
        </div>
      </div>

      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice</TableHead>
              <TableHead>Project</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Due</TableHead>
              <TableHead className="text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(5)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={7}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : filteredInvoices.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="h-32 text-center text-muted-foreground"
                >
                  No invoices match the current filters.
                </TableCell>
              </TableRow>
            ) : (
              filteredInvoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>
                    <Link
                      to={`/finance/invoices/${invoice.id}`}
                      className="font-medium hover:underline"
                    >
                      {[invoice.project?.number, invoice.invoice_number]
                        .filter(Boolean)
                        .join("-") || "Draft"}{" "}
                      - {invoice.title}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {invoice.project_id ? (
                      <Link
                        to={`/projects/${invoice.project_id}/finance`}
                        className="hover:underline"
                      >
                        {invoiceProjectLabel(invoice)}
                      </Link>
                    ) : (
                      "Standalone"
                    )}
                  </TableCell>
                  <TableCell>{invoice.customer?.name ?? "-"}</TableCell>
                  <TableCell>{statusBadge(invoice)}</TableCell>
                  <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
                  <TableCell>{formatDate(invoice.due_date)}</TableCell>
                  <TableCell className="text-right font-medium tabular-nums">
                    {formatCurrency(invoice.total_gross, invoice.currency)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
            <DialogDescription>
              Leave project empty to create a standalone invoice.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invoice-title">Title</Label>
              <Input
                id="invoice-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder={
                  selectedProject
                    ? formatProjectName(
                        selectedProject.name,
                        selectedProject.event_start_date,
                      )
                    : "Standalone invoice"
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Project</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No project</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {[project.number, project.name].filter(Boolean).join(" ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!selectedProject && (
              <div className="space-y-2">
                <Label>Language</Label>
                <Select
                  value={language}
                  onValueChange={(value: SupportedLanguage) =>
                    setLanguage(value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPORTED_LANGUAGES.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select
                value={currency}
                onValueChange={(value: InvoiceCurrency) => setCurrency(value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CHF">CHF — Swiss franc</SelectItem>
                  <SelectItem value="EUR">EUR — Euro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              offlineEdit
              onClick={handleCreate}
              disabled={createInvoice.isPending}
            >
              <Receipt className="h-4 w-4 mr-2" />
              Create Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
